import { createContext, useContext, useReducer, useCallback } from 'react';
import { api } from '../utils/api';
import { generateId, today } from '../utils/helpers';

const AppContext = createContext(null);

const initialState = {
  families: {},
  expenses: [],
  payments: [],
  contacts: [],
  notes: [],
  tickets: [],
  loading: true,
  error: null,
  toast: null,
};

function reducer(state, action) {
  switch (action.type) {
    case 'SET_STATE':   return { ...state, ...action.payload, loading: false };
    case 'SET_LOADING': return { ...state, loading: action.payload };
    case 'SET_ERROR':   return { ...state, error: action.payload, loading: false };
    case 'SHOW_TOAST':  return { ...state, toast: action.payload };
    case 'HIDE_TOAST':  return { ...state, toast: null };

    case 'UPDATE_FAMILY_NAME': {
      const f = { ...state.families };
      if (f[action.id]) f[action.id] = { ...f[action.id], name: action.name };
      return { ...state, families: f };
    }
    case 'ADD_MEMBER': {
      const f = { ...state.families };
      f[action.familyId] = { ...f[action.familyId], members: [...f[action.familyId].members, action.member] };
      return { ...state, families: f };
    }
    case 'UPDATE_MEMBER': {
      const f = { ...state.families };
      f[action.familyId] = {
        ...f[action.familyId],
        members: f[action.familyId].members.map(m => m.id === action.id ? { ...m, ...action.updates } : m)
      };
      return { ...state, families: f };
    }
    case 'REMOVE_MEMBER': {
      const f = { ...state.families };
      f[action.familyId] = { ...f[action.familyId], members: f[action.familyId].members.filter(m => m.id !== action.id) };
      return { ...state, families: f };
    }
    case 'ADD_EXPENSE':    return { ...state, expenses: [action.expense, ...state.expenses] };
    case 'UPDATE_EXPENSE': return { ...state, expenses: state.expenses.map(e => e.id === action.id ? { ...e, ...action.updates, updatedAt: new Date().toISOString() } : e) };
    case 'DELETE_EXPENSE': return { ...state, expenses: state.expenses.filter(e => e.id !== action.id) };
    case 'ADD_PAYMENT':    return { ...state, payments: [action.payment, ...state.payments] };
    case 'DELETE_PAYMENT': return { ...state, payments: state.payments.filter(p => p.id !== action.id) };
    case 'ADD_CONTACT':    return { ...state, contacts: [...state.contacts, action.contact] };
    case 'DELETE_CONTACT': return { ...state, contacts: state.contacts.filter(c => c.id !== action.id) };
    case 'ADD_NOTE':       return { ...state, notes: [action.note, ...state.notes] };
    case 'DELETE_NOTE':    return { ...state, notes: state.notes.filter(n => n.id !== action.id) };
    case 'ADD_TICKET':     return { ...state, tickets: [action.ticket, ...state.tickets] };
    case 'DELETE_TICKET':  return { ...state, tickets: state.tickets.filter(t => t.id !== action.id) };
    default: return state;
  }
}

export function AppProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const showToast = useCallback((message, type = 'info') => {
    dispatch({ type: 'SHOW_TOAST', payload: { message, type } });
    setTimeout(() => dispatch({ type: 'HIDE_TOAST' }), 3000);
  }, []);

  const loadState = useCallback(async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const data = await api.getState();
      dispatch({ type: 'SET_STATE', payload: data });
    } catch (err) {
      dispatch({ type: 'SET_ERROR', payload: err.message });
      showToast('Failed to connect to server', 'error');
    }
  }, [showToast]);

  const updateFamilyName = useCallback(async (familyId, name) => {
    dispatch({ type: 'UPDATE_FAMILY_NAME', id: familyId, name });
    await api.updateFamily(familyId, { name });
  }, []);

  const addMember = useCallback(async (familyId, memberData) => {
    const member = { ...memberData, id: generateId(), familyId };
    dispatch({ type: 'ADD_MEMBER', familyId, member });
    await api.addMember(member);
    return member;
  }, []);

  const updateMember = useCallback(async (familyId, memberId, updates) => {
    dispatch({ type: 'UPDATE_MEMBER', familyId, id: memberId, updates });
    await api.updateMember(memberId, updates);
  }, []);

  const removeMember = useCallback(async (familyId, memberId) => {
    dispatch({ type: 'REMOVE_MEMBER', familyId, id: memberId });
    await api.deleteMember(memberId);
  }, []);

  const addExpense = useCallback(async (data) => {
    const expense = { ...data, id: generateId(), createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() };
    dispatch({ type: 'ADD_EXPENSE', expense });
    await api.addExpense(expense);
    return expense;
  }, []);

  const updateExpense = useCallback(async (id, updates) => {
    dispatch({ type: 'UPDATE_EXPENSE', id, updates });
    const expense = { ...updates, updatedAt: new Date().toISOString() };
    await api.updateExpense(id, expense);
  }, []);

  const deleteExpense = useCallback(async (id) => {
    dispatch({ type: 'DELETE_EXPENSE', id });
    await api.deleteExpense(id);
  }, []);

  const addPayment = useCallback(async (data) => {
    const payment = { ...data, id: generateId(), date: data.date || today(), createdAt: new Date().toISOString() };
    dispatch({ type: 'ADD_PAYMENT', payment });
    await api.addPayment(payment);
    return payment;
  }, []);

  const deletePayment = useCallback(async (id) => {
    dispatch({ type: 'DELETE_PAYMENT', id });
    await api.deletePayment(id);
  }, []);

  const addContact = useCallback(async (data) => {
    const contact = { ...data, id: generateId() };
    dispatch({ type: 'ADD_CONTACT', contact });
    await api.addContact(contact);
    return contact;
  }, []);

  const deleteContact = useCallback(async (id) => {
    dispatch({ type: 'DELETE_CONTACT', id });
    await api.deleteContact(id);
  }, []);

  const addNote = useCallback(async (data) => {
    const note = { ...data, id: generateId(), date: new Date().toISOString() };
    dispatch({ type: 'ADD_NOTE', note });
    await api.addNote(note);
    return note;
  }, []);

  const deleteNote = useCallback(async (id) => {
    dispatch({ type: 'DELETE_NOTE', id });
    await api.deleteNote(id);
  }, []);

  const addTicket = useCallback(async (data) => {
    const ticketData = { ...data, id: generateId(), uploadDate: new Date().toISOString() };
    const result = await api.addTicket(ticketData);
    const ticket = { ...ticketData, filePath: result.filePath };
    delete ticket.fileData;
    dispatch({ type: 'ADD_TICKET', ticket });
    return ticket;
  }, []);

  const deleteTicket = useCallback(async (id) => {
    dispatch({ type: 'DELETE_TICKET', id });
    await api.deleteTicket(id);
  }, []);

  const resetAll = useCallback(async () => {
    await api.reset();
    await loadState();
  }, [loadState]);

  const exportData = useCallback(() => {
    const json = JSON.stringify({ families: state.families, expenses: state.expenses, payments: state.payments, contacts: state.contacts, notes: state.notes, tickets: state.tickets, exportedAt: new Date().toISOString() }, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `tour-expenses-${today()}.json`; a.click();
    URL.revokeObjectURL(url);
  }, [state]);

  const getFileUrl = useCallback((filePath) => {
    // filePath is stored as a full public URL e.g. https://api.benfortetech.com/uploads/abc_file.jpg
    return filePath || '';
  }, []);

  const value = {
    ...state,
    showToast, loadState,
    updateFamilyName, addMember, updateMember, removeMember,
    addExpense, updateExpense, deleteExpense,
    addPayment, deletePayment,
    addContact, deleteContact,
    addNote, deleteNote,
    addTicket, deleteTicket,
    resetAll, exportData, getFileUrl,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
}
